import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import {CdkDragDrop, transferArrayItem, CdkDragEnter, copyArrayItem, CdkDragStart} from '@angular/cdk/drag-drop';
import {untilDestroyed, UntilDestroy} from '@ngneat/until-destroy';
import {combineLatest, Observable, of, throwError} from 'rxjs';
import {map, catchError, shareReplay, take, switchMap, tap, finalize} from 'rxjs/operators';

import {ArraySort, ModalService, NotificationService} from '@shared';

import {
    EmployeeView,
    TaskAssigningIndicator,
    TaskView,
    TaskService,
    DragDropService,
    ActiveEmployeeService,
    AddressUpdateService,
    ProblemInstanceService,
    AddressFinderService,
    NavigationService,
    ShiftSplitService
} from 'planner-shared';
import {AdminTaskModalComponent} from "../admin-task-modal/admin-task-modal.component";
import {EmployeeAdminTaskAddress} from "../../../shared/models/employee-admin-task-address.model";
import {TimeWindowService} from "../../../shared/services/time-window/time-window.service";
import {AdminTaskUpdateResult} from "../../../shared/models/admin-task-update-result.model";
import {FeatureFlags, FeatureFlagService} from "@core";

@UntilDestroy()
@Component({
    selector: 'app-employee-assigned-tasks',
    templateUrl: './employee-assigned-tasks.component.html',
    styleUrls: ['./employee-assigned-tasks.component.scss'],
    standalone: false
})
export class EmployeeAssignedTasksComponent implements OnInit {
    private readonly taskService = inject(TaskService);
    private readonly dragDropService = inject(DragDropService);
    private readonly activeEmployeeService = inject(ActiveEmployeeService);
    private readonly modalService = inject(ModalService);
    private readonly addressUpdateService = inject(AddressUpdateService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly addressFinderService = inject(AddressFinderService);
    private readonly navigationService = inject(NavigationService);
    private readonly shiftSplitService = inject(ShiftSplitService);
    private readonly timeWindowService = inject(TimeWindowService);
    private readonly notificationService = inject(NotificationService);
    private readonly featureFlagService = inject(FeatureFlagService);

    @Input()
    public set employee(value: EmployeeView) {
        if (value) {
            this._employee = value;
            this.isEmployeeActive = value.isActive;
            this.loadEmployeeTasks();
            this.validateAssignedTasks();
            this.editing = false;
        }
    }

    public get employee(): EmployeeView {
        return this._employee;
    }

    @Input() public disableDrag: boolean;

    @Output() public undraggableStateChanged = new EventEmitter<boolean>();

    public isEmployeeActive: boolean;
    public taskAssigningIndicator: TaskAssigningIndicator;
    public employeeTasks$: Observable<TaskView[]>;
    public taskAddressPresent: Observable<boolean>;
    public rosterPresent: boolean;
    public editing = false;
    private readonly InitSortBy = 'fromTime';
    private readonly InitSortBySecondary = 'toTime';
    private employeeEditFixEnabled: boolean;

    private _employee: EmployeeView;

    constructor() {
        this.taskAssigningIndicator = <TaskAssigningIndicator>{assigning: false};
        this.taskAddressPresent = of(false);
    }

    ngOnInit(): void {
        this.taskService
            .getTaskAssigningIndicator()
            .pipe(untilDestroyed(this))
            .subscribe(indicator => {
                if (this.employee.workHourId !== indicator.workHourId) {
                    return;
                }

                this.taskAssigningIndicator = indicator;

                if (!indicator.assigning) {
                    this.loadEmployeeTasks();
                }
            });
        this.addressUpdateService.updateTasksSubject$.subscribe(() => {
            this.validateAssignedTasks();
        });

        this.problemInstanceService.rosterDepartmentIsPartOfInstance()
            .subscribe(value => this.rosterPresent = value.valueOf());

        this.shiftSplitService.updateEmployeeTasks$.subscribe(
            () => this.loadEmployeeTasks()
        );
        this.featureFlagService.isEnabled(FeatureFlags.FirstStepEmployeeEditOne)
            .subscribe(value => this.employeeEditFixEnabled = value);
    }

    public drop(event: CdkDragDrop<TaskView[]>): void {
        if (this.employee.dropDisabled) {
            event.item.data.numberOfEmployees++;
            this.dragDropService.allowDragDrop(event);
            return;
        }

        if (event.previousContainer !== event.container) {

            if (this.isDraggingOverlapsShiftSplit(event)) {
                return;
            }

            this.undraggableStateChanged.emit(true);

            if (event.item.data.numberOfEmployees >= 1) {
                copyArrayItem(
                    event.previousContainer.data,
                    event.container.data,
                    event.previousIndex,
                    event.currentIndex
                );
            } else {
                transferArrayItem(
                    event.previousContainer.data,
                    event.container.data,
                    event.previousIndex,
                    event.currentIndex
                );
            }

            this.taskService
                .assignGeneralTaskToEmployee(
                    this.employee.employeeId,
                    this.employee.workHourId,
                    event.item.data.originalTaskId
                )
                .pipe(
                    catchError(error => {
                        if (event.item.data.numberOfEmployees > 0) {
                            event.item.data.numberOfEmployees++;
                            event.container.data.splice(event.currentIndex, 1);
                        } else {
                            transferArrayItem(
                                event.container.data,
                                event.previousContainer.data,
                                event.currentIndex,
                                event.previousIndex
                            );
                        }
                        return throwError(error);
                    })
                )
                .subscribe(() => {
                    ArraySort.sort(event.container.data, this.InitSortBy, true, this.InitSortBySecondary);
                    this.activeEmployeeService.resetActiveEmployeeTasks();
                    this.undraggableStateChanged.emit(false);
                    this.validateAssignedTasks();
                });
        }
    }

    public entered(event: CdkDragEnter<TaskView>): void {
        if (this.employee.dropDisabled && event.container !== event.item.dropContainer) {
            this.dragDropService.resetPlaceholderPosition(event);
            this.dragDropService.disallowDragDrop(event);
        } else {
            this.dragDropService.showPlaceholder(event);
        }
    }

    public started(event: CdkDragStart<TaskView>): void {
        this.dragDropService.setDraggingTask(event.source.data);
    }

    public released(): void {
        this.dragDropService.setDraggingTask(null);
    }


    public openAdminTaskAddressModal(task: TaskView): void {
        this.modalService.openCustomModal(
            AdminTaskModalComponent,
            null,
            null,
            task,
            false
        );
    }

    public editTask(task: TaskView, event: Event) {
        if (!this.editing && this.employeeEditFixEnabled) {
            this.editing = true;
            task.editing = true;
            event.stopPropagation();
        } else if (!this.employeeEditFixEnabled) {
            task.editing = true;
            event.stopPropagation();
        }
        this.disableDrag = true;
        this.navigationService.disableNextButton(true);
    }

    public loadEmployeeTasks(): void {
        this.employeeTasks$ = this.taskService.getEmployeeTasks(this.employee.workHourId).pipe(
            map(tasks => {
                ArraySort.sort(tasks, this.InitSortBy, true, this.InitSortBySecondary);
                this.determineSplitOverlapStatus(tasks);
                return tasks;
            }),
            shareReplay({bufferSize: 1, refCount: true})
        );

        this.taskAddressPresent = this.employeeTasks$.pipe(
            map(tasks => tasks.some(task => task.streetAddressNameNumberAndLetter))
        );
    }

    public updateProblemInstance(updatedTasks: AdminTaskUpdateResult): void {
        this.editing = false;
        if (updatedTasks || !this.employeeEditFixEnabled) {
            this.problemInstanceService.getProblemInstance()
                .pipe(untilDestroyed(this))
                .subscribe(problemInstanceResult => {
                    updatedTasks.emTasksNotPatients.forEach(updateTask => {
                        const taskToUpdate = problemInstanceResult.problemInstance.emTasksNotPatients
                            .find(emTask => emTask.taskId === updateTask.taskId);
                        if (taskToUpdate) {
                            taskToUpdate.isActive = updateTask.isActive;
                            taskToUpdate.duration = updateTask.duration;
                        }
                    });
                    this.loadEmployeeTasks();
                });
        }
    }

    private validateAssignedTasks(): void {
        this.taskAssigningIndicator.assigning = true;

        combineLatest([
            this.problemInstanceService.getProblemInstance().pipe(take(1)),
            this.employeeTasks$.pipe()
        ]).pipe(
            switchMap(([instanceResult, employeeTasks]) => {
                const adminTasksRequest = employeeTasks
                    .filter(task => !!task.streetAddressNameNumberAndLetter)
                    .map(task => new EmployeeAdminTaskAddress(
                        task.taskId,
                        task.streetAddressNameNumberAndLetter,
                        task.zipCode
                    ));

                if (adminTasksRequest.length === 0) {
                    return of(null);
                }
                const tasks = instanceResult.problemInstance.emTasksNotPatients;

                return this.addressFinderService
                    .validateAdminTaskAddresses(adminTasksRequest, instanceResult.problemInstance.officeInfo)
                    .pipe(
                        tap(result => {
                            result.patientToAddressStatusList.forEach(status => {
                                const empTask =
                                    employeeTasks.find(t => t.taskId === status.patientId);
                                if (empTask) {
                                    empTask.addressIsValid = status.status;
                                }

                                const empTaskProblemInstance = tasks.find(t =>
                                    t.taskId === status.patientId);
                                if (empTaskProblemInstance) {
                                    empTaskProblemInstance.addressIsValid = status.status;
                                }
                            });
                        })
                    );
            }),
            finalize(() => {
                this.taskAssigningIndicator.assigning = false;
            }),
            untilDestroyed(this)
        ).subscribe();
    }

    private determineSplitOverlapStatus(tasks: TaskView[]) {
        if (tasks.some(task => task.isShiftSplitTask)) {
            const shiftSplitTask = tasks.find(task => task.isShiftSplitTask);
            tasks.forEach(task => {
                if (!task.isShiftSplitTask) {
                    if (this.timeWindowService
                        .isTaskOverlappingEmployeeTimeWindow(
                            task,
                            shiftSplitTask.fromTime,
                            shiftSplitTask.toTime,
                            this._employee,
                            false)) {
                        if (shiftSplitTask.isActive) {
                            if (!task.isActive) {
                                task.disabled = true;
                            }
                        } else {
                            if (task.isActive) {
                                shiftSplitTask.disabled = true;
                            }
                        }
                    }
                }
            });
        }
    }

    private isDraggingOverlapsShiftSplit(event: CdkDragDrop<TaskView[]>): boolean {
        let shouldPreventDragging = false;
        this.employeeTasks$
            .pipe(
                take(1),
                untilDestroyed(this)
            )
            .subscribe(employeeTasks => {
                if (employeeTasks.some(task => task.isShiftSplitTask && task.isActive)) {
                    const shiftSplitTask = employeeTasks.find(task => task.isShiftSplitTask);
                    if (this.timeWindowService
                        .isTaskOverlappingEmployeeTimeWindow(
                            event.item.data,
                            shiftSplitTask.fromTime,
                            shiftSplitTask.toTime,
                            this.employee,
                            false)) {
                        shouldPreventDragging = true;
                        event.item.data.numberOfEmployees++;
                        this.notificationService.warning('SPLIT_SHIFT_ASSIGN_TASK_OVERLAP');
                        return;
                    }
                }
            });
        return shouldPreventDragging;
    }
}
